/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useEffect, useRef, useState } from 'react';
import { usePublications } from '@features/property-management/components/publications-center/use-publications';
import { buildRows } from '@features/property-management/components/publications-center/rows';
import { PublicationRowItem } from '@features/property-management/components/publications-center/PublicationRowItem';
import { ConfirmDialog } from '@features/property-management/components/publications-center/ConfirmDialog';
import type { PublicationRow } from '@features/property-management/components/publications-center/types';
import type { CatalogPublication } from '../types';
import { Text } from '@/shared/ui';

/**
 * Derives the publications-center row key for a catalog entry.
 *
 * @param entry - The catalog publication.
 * @returns The row key, or null when the entry cannot be edited inline.
 */
function rowKeyForEntry(entry: CatalogPublication): string | null {
  const id = entry.publication_id;
  if (!id) return null;
  if (entry.portal === 'facebook_feed') return `facebook-post-${id}`;
  if (entry.portal === 'facebook_marketplace') return `facebook-listing-${id}`;
  if (entry.portal === 'olx') return 'olx';
  return null;
}

interface Props {
  entry: CatalogPublication;
  agencyId: string;
  onDone: () => void;
  onSaved?: () => void;
}

/**
 * Inline editor that reuses the publications-center editing flow for a single
 * catalog entry.
 *
 * @param props - Entry, agency scope, and lifecycle callbacks.
 * @returns The inline edit element.
 */
export function PublicationInlineEdit({ entry, agencyId, onDone, onSaved }: Props) {
  const propertyImages = entry.property_image ? [entry.property_image] : [];
  const pub = usePublications(entry.property_id, agencyId, propertyImages);
  const [confirmRow, setConfirmRow] = useState<PublicationRow | null>(null);
  const editFiredRef = useRef(false);

  function getPortalLabel(portal: string) {
    const key = `publications.portal.${portal}` as const;
    const label = pub.t(key as any);
    return label !== key ? label : portal.toUpperCase();
  }

  function getStatusLabel(status: string) {
    const key = `publications.status.${status}` as const;
    const label = pub.t(key as any);
    return label !== key ? label : status;
  }

  const targetKey = rowKeyForEntry(entry);
  const rows = buildRows(pub.publications, getPortalLabel);
  const targetRow = targetKey ? rows.find((r) => r.key === targetKey) : null;

  useEffect(() => {
    if (!pub.loading && targetRow && !editFiredRef.current) {
      editFiredRef.current = true;
      pub.handleEdit(targetRow);
    }
  }, [pub.loading, targetRow?.key]);

  if (pub.loading) {
    return (
      <div className="flex items-center justify-center py-(--space-12)">
        <Text component="span"
          className="text-xs animate-pulse"
          style={{ color: 'var(--text-tertiary)' }}
        >
          {pub.t('publications.loading')}
        </Text>
      </div>
    );
  }

  if (!targetRow) {
    return (
      <div className="flex items-center justify-center py-(--space-12)">
        <Text component="span" className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          {pub.t('publications.noPublications')}
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-(--space-2)">
      <PublicationRowItem
        row={targetRow}
        t={pub.t}
        connectedPortals={pub.connectedPortals}
        removing={pub.removing}
        updating={pub.updating}
        editingRow={pub.editingRow}
        editText={pub.editText}
        setEditText={pub.setEditText}
        loadingEditContent={pub.loadingEditContent}
        editImages={pub.editImages}
        editImagesOriginal={pub.editImagesOriginal}
        setEditImages={pub.setEditImages}
        getStatusLabel={getStatusLabel}
        onEdit={pub.handleEdit}
        onConfirmRemove={setConfirmRow}
        compact
        onEditSave={async (row) => {
          await pub.handleEditSave(row);
          onSaved?.();
          onDone();
        }}
        onCancelEdit={() => {
          pub.cancelEdit();
          onDone();
        }}
      />

      {confirmRow && (
        <ConfirmDialog
          message={pub.t('publications.actions.confirmRemoveMessage', { portal: confirmRow.label })}
          onConfirm={async () => {
            await pub.handleRemove(confirmRow);
            setConfirmRow(null);
            onDone();
          }}
          onCancel={() => setConfirmRow(null)}
          confirmLabel={pub.t('publications.actions.confirm')}
          cancelLabel={pub.t('publications.actions.cancel')}
        />
      )}
    </div>
  );
}
